import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import Page from '../../../components/layout/Page'
import Button from '../../../components/ui/Button'
import Card from '../../../components/ui/Card'
import Icon from '../../../components/AppIcon'
import { db, storage } from '../../../firebase'
import { doc, getDoc } from 'firebase/firestore'
import { ref as storageRef, getBlob } from 'firebase/storage'
import { normalizeTx } from '../lib/goldTx'
import { getCaptureGroups, waitForContractImages } from '../lib/contractImages.mjs'
import CaptureImage from '../components/CaptureImage'
import CustomerPhoto from '../components/CustomerPhoto'

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1'])
const LOCAL_TX_KEY = 'artgold.mock.exchange.transactions.v1'

function getIsLocalMockMode() {
  if (typeof window === 'undefined') return false
  return LOCAL_HOSTNAMES.has(window.location.hostname)
}

function readLocalJson(key, fallback) {
  if (typeof window === 'undefined') return fallback
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : fallback
  } catch {
    return fallback
  }
}

async function loadSavedTransaction(transactionId) {
  if (getIsLocalMockMode()) {
    const localTx = readLocalJson(LOCAL_TX_KEY, {})?.[transactionId]
    if (!localTx) throw new Error('Local transaction not found')
    return localTx
  }
  const snap = await getDoc(doc(db, 'gold_transactions', transactionId))
  if (!snap.exists()) throw new Error('Transaction not found')
  return snap.data()
}

async function loadContractTemplate() {
  const snap = await getDoc(doc(db, 'gold_settings', 'contract_template'))
  return snap.exists() ? snap.data() : null
}

function loadStoredPhoto(path) {
  return getBlob(storageRef(storage, path))
}

function toDate(value) {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function formatDateTime(value) {
  const d = toDate(value)
  if (!d) return '—'
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatMoney(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '$0.00'
  return `$${num.toFixed(2)}`
}

// Item rows in either shape (migrated shop-app records vs portal-created)
function itemView(it) {
  if (it.weightGrams != null || it.karat != null) {
    const isSilver = Number(it.karat) === 0
    return {
      metal: isSilver ? 'silver' : 'gold',
      karatLabel: isSilver ? '999' : `${it.karatValue || it.karat}K`,
      type: it.description || '—',
      weight: Number(it.weightGrams) || 0,
      pricePerGram: it.pricePerGram,
      subtotal: it.subtotal,
    }
  }
  const isSilver = it.metal === 'silver'
  return {
    metal: it.metal || 'gold',
    karatLabel: isSilver ? '999' : `${it.karatWhole || ''}.${it.karatDecimal || ''}`.replace(/\.$/, '') || '—',
    type: it.itemType || '—',
    weight: Number(it.weight) || 0,
    pricePerGram: null,
    subtotal: null,
  }
}

// Migrated attachments live as Storage paths in tx.files — stream via SDK
// (paths carry no public token), same approach as the Transactions screen.
function StoragePhoto({ path, label, loadStoragePhoto }) {
  const [url, setUrl] = useState(null)
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    let alive = true
    let objectUrl = null
    setUrl(null)
    setFailed(false)
    Promise.resolve().then(() => loadStoragePhoto(path))
      .then((blob) => { if (alive) { objectUrl = URL.createObjectURL(blob); setUrl(objectUrl) } })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false; if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [path, loadStoragePhoto])
  return (
    <div className="space-y-1" data-contract-image-state={url ? 'loaded' : failed ? 'failed' : 'loading'}>
      <div className="rounded-lg border border-border/60 bg-white overflow-hidden flex items-center justify-center min-h-24">
        {url ? (
          <CaptureImage src={url} alt={label} className="w-full object-contain" />
        ) : failed ? (
          <p role="alert" className="p-3 text-sm text-destructive">Could not load this attachment. Reload the contract before printing.</p>
        ) : (
          <Icon name="Loader2" size={20} className="text-muted-foreground animate-spin m-6" />
        )}
      </div>
      <p className="text-[11px] text-muted-foreground truncate">{label}</p>
    </div>
  )
}

export default function ExchangeDeskContractPage({
  loadTransaction = loadSavedTransaction,
  loadTemplate = loadContractTemplate,
  loadStoragePhoto = loadStoredPhoto,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { transactionId } = useParams()

  const shouldAutoPrint = useMemo(() => {
    const q = new URLSearchParams(location.search)
    return q.get('print') === '1'
  }, [location.search])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tx, setTx] = useState(null)
  const [template, setTemplate] = useState(null)
  const [templateLoading, setTemplateLoading] = useState(true)
  const [preparingPrint, setPreparingPrint] = useState(false)
  const [printError, setPrintError] = useState('')
  const contractRef = useRef(null)
  const printRequestRef = useRef(null)
  const autoPrintRef = useRef(null)

  useEffect(() => {
    autoPrintRef.current = null
    setPreparingPrint(false)
    setPrintError('')
    return () => {
      printRequestRef.current?.abort()
      printRequestRef.current = null
    }
  }, [transactionId])

  // Company info / terms / footer come from the admin-editable template
  // (gold_settings/contract_template — migrated from the shop app).
  useEffect(() => {
    let cancelled = false
    setTemplateLoading(true)
    Promise.resolve().then(() => loadTemplate())
      .then((data) => { if (!cancelled) setTemplate(data) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setTemplateLoading(false) })
    return () => { cancelled = true }
  }, [loadTemplate])

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        if (!transactionId) throw new Error('Missing transactionId')

        const data = await loadTransaction(transactionId)
        if (!data) throw new Error('Transaction not found')
        if (!cancelled) setTx({ ...data, id: transactionId })
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load contract')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [transactionId, loadTransaction])

  const requestPrint = useCallback(async () => {
    if (loading || templateLoading || error || !tx || tx.id !== transactionId || printRequestRef.current) return
    const controller = new AbortController()
    printRequestRef.current = controller
    setPreparingPrint(true)
    setPrintError('')
    try {
      await waitForContractImages(contractRef.current, { signal: controller.signal })
      if (!controller.signal.aborted) window.print()
    } catch (err) {
      if (!controller.signal.aborted) setPrintError(err?.message || 'Could not prepare the contract for printing. Please retry.')
    } finally {
      if (printRequestRef.current === controller) {
        printRequestRef.current = null
        setPreparingPrint(false)
      }
    }
  }, [loading, templateLoading, error, tx, transactionId])

  useEffect(() => {
    if (!shouldAutoPrint || loading || templateLoading || error || !tx || tx.id !== transactionId) return
    if (autoPrintRef.current === transactionId) return
    autoPrintRef.current = transactionId
    requestPrint()
  }, [shouldAutoPrint, loading, templateLoading, error, tx, transactionId, requestPrint])

  const captures = getCaptureGroups(tx?.captures || {})
  const items = Array.isArray(tx?.items) ? tx.items : []
  const customer = tx?.customer || {}
  const prices = tx?.prices || {}
  // Money summary through the shared normalizer so MIGRATED shop-app records
  // print real numbers on the police report (not zeros).
  const norm = tx ? normalizeTx(tx.id || transactionId, tx) : null
  const goldPriceLine = prices.gold ?? tx?.goldPrice24k
  const migratedFiles = Array.isArray(tx?.files) ? tx.files.filter((f) => f?.path) : []

  return (
    <Page size="full" title="Exchange Desk Contract" subtitle={tx?.transactionNumber || transactionId || ''}>
      <style>{`
        @media print {
          header, nav, .no-print { display: none !important; }
          main { padding: 0 !important; max-width: none !important; }
          .print-card { border: none !important; box-shadow: none !important; }
          body { background: white !important; }
        }
      `}</style>

      <div className="no-print mb-4 flex items-center justify-between gap-2">
        <Button variant="ghost" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate('/exchange-desk')}>
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            iconName="Printer"
            iconPosition="left"
            disabled={loading || templateLoading || !!error || !tx || preparingPrint}
            onClick={requestPrint}
          >
            {preparingPrint ? 'Preparing images…' : 'Print / Save PDF'}
          </Button>
        </div>
      </div>

      {preparingPrint && <p role="status" className="no-print mb-4 text-sm text-foreground">Waiting for the contract images before printing…</p>}
      {printError && <p role="alert" className="no-print mb-4 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{printError}</p>}

      {loading ? (
        <Card variant="solid" className="print-card text-center py-16">
          <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Loading contract...</p>
        </Card>
      ) : error ? (
        <Card variant="solid" className="print-card bg-destructive/15 border border-destructive/30">
          <div className="flex items-start gap-3">
            <Icon name="AlertCircle" size={18} className="text-destructive mt-0.5" />
            <div>
              <p className="font-semibold text-destructive">Could not load contract</p>
              <p className="text-sm text-destructive mt-1">{error}</p>
            </div>
          </div>
        </Card>
      ) : !tx ? (
        <Card variant="solid" className="print-card">
          <p className="text-muted-foreground">No contract data.</p>
        </Card>
      ) : (
        <Card ref={contractRef} variant="solid" className="print-card border border-border/60 shadow-elevation-1">
          <div className="p-6 space-y-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                {template?.companyName ? (
                  <>
                    <p className="text-lg font-bold text-foreground">{template.companyName}</p>
                    <p className="text-xs text-muted-foreground">
                      {[template.companyAddress, template.companyPhone, template.companyEmail]
                        .filter(Boolean)
                        .join(' · ')}
                      {template.companyTaxId ? ` · Tax ID: ${template.companyTaxId}` : ''}
                    </p>
                    <h1 className="text-2xl font-bold text-foreground mt-2">Gold Purchase Contract</h1>
                    <p className="text-sm text-muted-foreground">Legal record — for law enforcement submission</p>
                  </>
                ) : (
                  <>
                    <h1 className="text-2xl font-bold text-foreground">Gold Purchase Contract</h1>
                    <p className="text-sm text-muted-foreground mt-1">For law enforcement submission</p>
                  </>
                )}
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Transaction</div>
                <div className="text-lg font-semibold text-foreground">{tx.transactionNumber}</div>
                <div className="text-xs text-muted-foreground mt-1">{formatDateTime(tx.createdAt)}</div>
                <div className="text-xs text-muted-foreground">Envelope: {tx.envelopeNumber || '—'}</div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-2">Seller Information</h2>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div><span className="text-foreground font-medium">Name:</span> {customer.name || `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || '—'}</div>
                  <div><span className="text-foreground font-medium">Phone:</span> {customer.phone || '—'}</div>
                  <div><span className="text-foreground font-medium">Email:</span> {customer.email || '—'}</div>
                  <div><span className="text-foreground font-medium">Address:</span> {[
                    customer.street,
                    customer.city,
                    customer.state,
                    customer.zip
                  ].filter(Boolean).join(', ') || '—'}</div>
                </div>
              </div>

              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-2">ID Details</h2>
                <div className="text-sm text-muted-foreground space-y-1">
                  <div><span className="text-foreground font-medium">ID Type:</span> {customer.idType || '—'}</div>
                  <div><span className="text-foreground font-medium">ID Number:</span> {customer.idNumber || '—'}</div>
                  <div><span className="text-foreground font-medium">State/Country:</span> {customer.idState || customer.idCountry || '—'}</div>
                  <div><span className="text-foreground font-medium">Expiration:</span> {customer.idExpiration || '—'}</div>
                  {customer.idOtherReason ? (
                    <div><span className="text-foreground font-medium">Other:</span> {customer.idOtherReason} {customer.idOtherDetails ? `(${customer.idOtherDetails})` : ''}</div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-border/60 p-4">
              <h2 className="text-sm font-semibold text-foreground mb-3">Items</h2>
              <div className="overflow-auto rounded-lg border border-border bg-background">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-muted-foreground">
                    <tr>
                      <th className="p-2 text-left font-semibold">Metal</th>
                      <th className="p-2 text-left font-semibold">Karat</th>
                      <th className="p-2 text-left font-semibold">Type</th>
                      <th className="p-2 text-right font-semibold">Weight (g)</th>
                      <th className="p-2 text-right font-semibold">Price/g</th>
                      <th className="p-2 text-right font-semibold">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items.map((it, idx) => {
                      const v = itemView(it)
                      return (
                        <tr key={idx}>
                          <td className="p-2 text-muted-foreground">{v.metal}</td>
                          <td className="p-2 text-muted-foreground">{v.karatLabel}</td>
                          <td className="p-2 text-muted-foreground">{v.type}</td>
                          <td className="p-2 text-right text-muted-foreground">{v.weight.toFixed(3)}</td>
                          <td className="p-2 text-right text-muted-foreground">{v.pricePerGram != null ? formatMoney(v.pricePerGram) : '—'}</td>
                          <td className="p-2 text-right text-muted-foreground">{v.subtotal != null ? formatMoney(v.subtotal) : '—'}</td>
                        </tr>
                      )
                    })}
                    {items.length === 0 ? (
                      <tr>
                        <td className="p-3 text-muted-foreground" colSpan={6}>No items recorded.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>

              {/* Customer-signed document: gross value and deságio are internal
                  (admin reports only) — the contract states weight and payout. */}
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                  <div className="text-xs text-muted-foreground">Total Weight</div>
                  <div className="font-semibold text-foreground">{Number(norm?.weightGrams || 0).toFixed(2)} g</div>
                </div>
                <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                  <div className="text-xs text-muted-foreground">Payout</div>
                  <div className="font-semibold text-foreground">{formatMoney(norm?.payout)}</div>
                </div>
              </div>

              <div className="mt-3 text-xs text-muted-foreground">
                Prices source: {prices.source || (tx.goldPrice24k ? 'shop app record' : '—')} • Gold: {goldPriceLine ? `$${Number(goldPriceLine).toFixed(2)}/g` : '—'} • Silver: {prices.silver ? `$${Number(prices.silver).toFixed(2)}/g` : '—'}
              </div>
            </div>

            {(migratedFiles.length === 0 || captures.customerPhoto) && (
              <CustomerPhoto src={captures.customerPhoto} skipped={tx.customerPhotoSkipped} />
            )}

            {/* Migrated shop-app records: photos & signatures live as Storage
                paths in tx.files — the police report must include them too. */}
            {migratedFiles.length > 0 && (
              <div className="rounded-xl border border-border/60 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-3">Photos &amp; Signatures</h2>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {migratedFiles.map((f, i) => (
                    <StoragePhoto key={f.path} path={f.path} label={f.label || f.fileType || `File ${i + 1}`} loadStoragePhoto={loadStoragePhoto} />
                  ))}
                </div>
              </div>
            )}

            {migratedFiles.length === 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border/60 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-3">ID Photo</h2>
                {captures.idFront ? (
                  <CaptureImage src={captures.idFront} alt="ID Photo" className="w-full rounded-lg border border-border/60 object-contain bg-white" />
                ) : (
                  <div className="text-sm text-muted-foreground">No ID photo captured.</div>
                )}
              </div>
              <div className="rounded-xl border border-border/60 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-3">Item Photos</h2>
                {captures.itemPhotos.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2">
                    {captures.itemPhotos.map((src, idx) => (
                      <CaptureImage key={idx} src={src} alt={`Item Photo ${idx + 1}`} className="w-full rounded-lg border border-border/60 object-cover bg-white aspect-square" />
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No item photos captured.</div>
                )}
              </div>
            </div>
            )}

            {migratedFiles.length === 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="rounded-xl border border-border/60 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-3">Seller Signature</h2>
                {captures.sellerSig ? (
                  <CaptureImage src={captures.sellerSig} alt="Seller signature" className="w-full rounded-lg border border-border/60 object-contain bg-white" />
                ) : (
                  <div className="text-sm text-muted-foreground">No signature captured.</div>
                )}
              </div>
              <div className="rounded-xl border border-border/60 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-3">Buyer Signature</h2>
                {captures.buyerSig ? (
                  <CaptureImage src={captures.buyerSig} alt="Buyer signature" className="w-full rounded-lg border border-border/60 object-contain bg-white" />
                ) : (
                  <div className="text-sm text-muted-foreground">No signature captured.</div>
                )}
              </div>
            </div>
            )}

            {customer.notes ? (
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-2">Notes</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{String(customer.notes)}</p>
              </div>
            ) : null}

            {template?.termsAndConditions ? (
              <div className="rounded-xl border border-border/60 bg-muted/20 p-4">
                <h2 className="text-sm font-semibold text-foreground mb-2">Terms &amp; Conditions</h2>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{template.termsAndConditions}</p>
              </div>
            ) : null}

            <div className="text-xs text-muted-foreground">
              {template?.footerText ? <p className="text-sm text-foreground mb-1">{template.footerText}</p> : null}
              Generated from the portal at {new Date().toLocaleString('en-US')}.
            </div>
          </div>
        </Card>
      )}
    </Page>
  )
}
